import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import {
  Home, Grid3X3, Search, User, ShoppingCart, X, ChevronRight, Package
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const categoryIcons = {
  'Sanitaryware': '🚿', 'Electrical': '⚡', 'Lightings': '💡',
  'Safety': '🛡️', 'Tools': '🔧', 'Hardware': '🔨',
  'Cabins': '🏗️', 'Bollards': '🔴', 'Automation': '⚙️',
  'Plumbing': '🔩', 'Paints': '🎨', 'Power Tools': '🛠️',
};

const BottomNav = () => {
  const { user, cart, categories } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCategories, setShowCategories] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Hide on admin pages
  if (location.pathname.startsWith('/admin') || location.pathname === '/gpgt-portal') return null;

  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const currentPath = location.pathname;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSearch(false);
    }
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/', active: currentPath === '/' },
    { icon: Grid3X3, label: 'Categories', path: null, active: showCategories, onClick: () => { setShowCategories(!showCategories); setShowSearch(false); } },
    { icon: Search, label: 'Search', path: null, active: showSearch, onClick: () => { setShowSearch(!showSearch); setShowCategories(false); } },
    { icon: User, label: 'Account', path: user ? '/account' : '/login', active: currentPath === '/account' || currentPath === '/login' },
    { icon: ShoppingCart, label: 'Cart', path: '/cart', active: currentPath === '/cart', badge: cartItemCount },
  ];

  return (
    <>
      {/* Category Overlay */}
      {showCategories && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCategories(false)} />
          <div className="absolute bottom-[64px] left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto shadow-2xl animate-slide-up">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-[#0c1b44] text-lg">Categories</h3>
              <button onClick={() => setShowCategories(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="p-2">
              <Link
                to="/products"
                onClick={() => setShowCategories(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-[#0c1b44]/10 rounded-xl flex items-center justify-center">
                  <Package className="h-5 w-5 text-[#0c1b44]" />
                </div>
                <span className="font-semibold text-[#0c1b44]">All Products</span>
                <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
              </Link>
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/products?category=${cat.id}`}
                  onClick={() => setShowCategories(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-lg">
                    {categoryIcons[cat.name] || '📦'}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-gray-800">{cat.name}</span>
                    {cat.children && cat.children.length > 0 && (
                      <p className="text-xs text-gray-400">{cat.children.length} subcategories</p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search Overlay */}
      {showSearch && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSearch(false)} />
          <div className="absolute bottom-[64px] left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[#0c1b44] text-lg">Search Products</h3>
                <button onClick={() => setShowSearch(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleSearch} className="relative">
                <Input
                  type="text"
                  placeholder="Search products, categories, brands..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-12 bg-gray-50 border-2 border-gray-200 rounded-xl pr-12 text-[#0c1b44] placeholder:text-gray-400 focus:border-[#0c1b44]"
                  autoFocus
                />
                <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#0c1b44] rounded-lg flex items-center justify-center text-white">
                  <Search className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                onClick={() => {
                  if (item.onClick) item.onClick();
                  else if (item.path) navigate(item.path);
                }}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative transition-colors ${
                  item.active ? 'text-[#0c1b44]' : 'text-gray-400'
                }`}
              >
                <div className="relative">
                  <Icon className={`h-5 w-5 ${item.active ? 'stroke-[2.5]' : ''}`} />
                  {item.badge > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#0c1b44] text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium ${item.active ? 'font-bold' : ''}`}>{item.label}</span>
                {item.active && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#0c1b44] rounded-full" />}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
